<template>
  <section
    id="cart-overview"
    class="cart-overview"
  >
    <h3 class="cart-overview__title">
      {{ $t('title') }}
    </h3>

    <transition name="transition-fade">
      <div
        v-if="$store.state.cartLoading"
        class="cart-overview__loading"
      >
        <loader-spinner :size="1.5" />
      </div>
    </transition>

    <transition name="transition-fade">
      <div v-if="!$store.state.cartLoading">
        <div
          v-show="$store.state.cart.reservations.count > 0"
          key="articles"
          class="cart-overview__ticket"
        >
          <p class="cart-overview__ticket-info">
            {{ date($store.state.cart.reservations.date) }}<br>
            {{ $t('arrivalSlot') }}: {{ gapTime }}<br>
            {{ $t('quantity.title') }}: {{ $store.state.cart.reservations.count }} {{ $tc('quantity.unit', $store.state.cart.reservations.count) }}
          </p>

          <button
            class="cart-overview__delete"
            @click="removeReservations"
          >
            <icon type="delete" />
            {{ $tc('removeReservations', $store.state.reservationsAmount) }}
          </button>
        </div>

        <action-link
          v-show="$store.state.cart.reservations.count > 0 && allValidArticles()"
          key="continue"
          color="primary"
          @click="continueToCheckout()"
        >
          {{ $t('continue.text') }}
          <icon
            type="chevron-right"
            :size="0.75"
          />
        </action-link>
        <p
          v-show="$store.state.cart.reservations.count > 0 && !allValidArticles()"
          key="error"
          class="cart-overview__error"
        >
          {{ $t('invalidArticles') }}
        </p>

        <p
          v-show="$store.state.cart.reservations.count > 0"
          key="note"
          class="cart-overview__note"
        >
          {{ $t('note') }}
          <button
            class="link"
            @click="scrollToVisitorInfo"
          >
            {{ $t('changeNow') }}
          </button>
        </p>

        <p
          v-show="$store.state.cart.reservations.count <= 0"
          key="empty"
          class="cart-overview__empty"
        >
          {{ $t('empty') }}
        </p>
      </div>
    </transition>
  </section>
</template>

<script>
import jump from 'jump.js'

import actionLink from './actionLink.vue'
import icon from './icon.vue'
import loaderSpinner from './loaderSpinner.vue'

import dateHelper from '../functions/dateHelper.js'
import errors from '../functions/errors.js'
import eventBus from '../eventBus.js'
import gapFromTime from '../functions/gapFromTime.js'
import humanDate from '../functions/humanDate.js'
import timeFromGap from '../functions/timeFromGap.js'

export default {
  name: 'CCartOverviewReservations',

  components: {
    actionLink,
    icon,
    loaderSpinner
  },

  computed: {
    gapTime () {
      return timeFromGap(this.$store.state.cart.reservations.gap)
    }
  },

  created () {
    const dontResetSelection = !isNaN(parseInt(this.$route.params.people))
    this.$store.dispatch('getCart', { dontResetSelection })
  },

  methods: {
    scrollToVisitorInfo () {
      jump('#visitor-info', { duration: 500 })
    },

    allValidArticles () {
      const cartDate = new Date(this.$store.state.cart.reservations.date).setHours(0, 0, 0, 0)
      const today = dateHelper.getDate()

      // cart is from the past
      if (cartDate < today) {
        return false
      }

      if (cartDate === today) {
        // Cat is for today
        const gap = gapFromTime(new Date())
        if (gap > this.$store.state.cart.reservations.gap) {
          return false
        }
      }
      return true
    },

    date (date) {
      const prettyDate = humanDate(date, this.$i18n.locale)
      return `${prettyDate.weekday.short}, ${prettyDate.date.long}`
    },

    removeReservations () {
      this.$store.dispatch('removeFromCart', { endpoint: 'reservations' })
    },

    continueToCheckout () {
      if (this.allValidArticles()) {
        this.$router.push({ name: 'reservation-overview-' + this.$i18n.locale })
      } else {
        eventBus.emit('handleError', errors(this.$i18n.locale)[1496305904])
      }
    }
  }
}
</script>

<i18n locale="en">
{
  "title": "Overview",
  "arrivalSlot": "Time Slot of Arrival",
  "quantity": {
    "title": "Quantity",
    "unit": "Person | Persons"
  },
  "time": "Time",
  "removeReservations": "Delete Reservation | Delete Reservations",
  "continue": {
    "text": "Reserve now"
  },
  "note": "To change the number of reservations simply enter a new number of reservations in Visitor Information. In order to change the date or time frame just select a different time slot.",
  "changeNow": "Change now!",
  "empty": "Please select the desired number of reservations, and the time slot of arrival.",
  "invalidArticles": "Your articles have been expired. Please select a new arrival time."
}
</i18n>

<i18n locale="de">
{
  "title": "Reservierung",
  "arrivalSlot": "Einlasszeit",
  "quantity": {
    "title": "Anzahl",
    "unit": "Person | Personen"
  },
  "time": "Uhrzeit",
  "removeReservations": "Reservierung löschen | Reservierungen löschen",
  "continue": {
    "text": "Jetzt reservieren"
  },
  "note": "Um die Personenanzahl zu ändern, müssen Sie lediglich bei den Besucherdaten eine neue Anzahl auswählen. Um das Datum oder Zeitfenster zu verändern, müssen Sie lediglich in der Ankunfts­zeitraum­auswahl ein neues Zeitfenster anklicken.",
  "changeNow": "Jetzt ändern!",
  "empty": "Bitte wählen Sie die gewünschte Anzahl an Reservierungen und die Ankunftszeit.",
  "invalidArticles": "Ihre Artikel sind abgelaufen. Bitte wählen Sie eine neue Ankunftszeit."
}
</i18n>

<style lang="scss">
  .cart-overview__loading {
    text-align: center;
    padding: 1.25em;
  }
</style>
